import { Button, Text, useToast } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { FunctionComponent, useState } from 'react'
import { Route } from '../../config/router'
import { CheckPoint, PaymentMethodType } from '../../model'
import {
  createDocuments,
  resetCheckoutError,
  resetPreferredCardReason,
  selectPurchaseMethod,
  setCardScanCheckpoint,
  setDisplayProcessingModal,
  setEndConfirmTime,
  setStartConfirmTime,
  updatePaymentOption,
  updatePaymentOptions,
  useAppDispatch,
  useApplePay,
  useAppRouter,
  useIsConsumerLendingAutopayEnabled,
  useOAuthStateSelector,
  useSelectedPaymentMethod,
  useSelectedPaymentOption,
  useConfirmCheckoutArgs,
  useConfirmCheckoutMutation,
  useIsPaymonthlySUPOrder,
} from '../../state'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { PaymentMethodTypes, TrackingEvent } from '../../model/monitoring'
import { ErrorCode, isPortalApiError } from '@afterpaytouch/portal-api'
import { oAuthAuthorization } from './helper'
import { useCheckoutSizes } from '../../utils/responsive'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { getConfirmCheckoutErrorHandling } from '../../utils/redirects'
import { useGetIsAutoPayAgreed } from '../AutopayToggle/hooks'

const DetailsUpdatedMessage: FunctionComponent = () => {
  const { smallTextSize } = useCheckoutSizes()
  const { t } = useTranslation()
  return (
    <>
      <Text size={smallTextSize} bold testNameSpace='loan-details-updated'>
        {t('consumerLending:til.loanDetailsUpdated.heading')}
      </Text>
      <Text size={smallTextSize}>{t('consumerLending:til.loanDetailsUpdated.content')}</Text>
    </>
  )
}

export const TruthInLendingButton: FunctionComponent = () => {
  const { t } = useTranslation()
  const { push } = useAppRouter()
  const setToast = useToast()
  const [clicked, setClicked] = useState<boolean>(false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const selectedPaymentOption = useSelectedPaymentOption()
  const dispatch = useAppDispatch()
  const [confirmCheckoutMutation] = useConfirmCheckoutMutation()
  const confirmArgs = useConfirmCheckoutArgs()
  const applePayHelper = useApplePay()
  const oAuthState = useOAuthStateSelector()
  const autopayEnabled = useIsConsumerLendingAutopayEnabled()
  const [loanDetailsUpdated, setLoanDetailsUpdated] = useState<boolean>(false)
  const isSUPPMOrder = useIsPaymonthlySUPOrder()
  const autoPayAgreed = useGetIsAutoPayAgreed()

  const updateLoanDetails = async (): Promise<void> => {
    try {
      await dispatch(updatePaymentOptions()).unwrap()
      await dispatch(updatePaymentOption(selectedPaymentOption?.id)).unwrap()
      await dispatch(createDocuments({ autopay: autopayEnabled })).unwrap()

      setLoanDetailsUpdated(true)
      dispatch(resetCheckoutError())
      window?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      setToast({ message: <DetailsUpdatedMessage />, kind: 'Success' })
      setClicked(false)
    } catch (e) {
      push(Route.TERMINAL_ERROR)
    }
  }

  const handleCheckoutConfirmationError = async (error): Promise<void> => {
    if (isPortalApiError(error)) {
      const { errorCode } = error
      if (errorCode === ErrorCode.InstallmentAmountsMismatch) {
        if (!loanDetailsUpdated) {
          await updateLoanDetails()
          return
        } else {
          push(Route.TERMINAL_ERROR)
        }
      }

      const { route } = getConfirmCheckoutErrorHandling({
        errorCode,
      })
      if (Route.CARD_SCAN === route) {
        setCardScanCheckpoint(CheckPoint.CheckoutConfirm)
      }
      setClicked(false)
      push(route ?? Route.SUMMARY)
    } else {
      capture.warning('Unexpected error during payment confirmation', { ...error })
      push(Route.TERMINAL_ERROR)
    }
  }

  const finaliseCheckout = async (): Promise<void> => {
    try {
      const confirmResponse = await confirmCheckoutMutation(confirmArgs).unwrap()
      await oAuthAuthorization(dispatch, logEvent, oAuthState)
      dispatch(setEndConfirmTime(performance.now()))
      if (confirmResponse.status === 'FAILURE') {
        push(Route.TERMINAL_ERROR)
      } else {
        dispatch(setDisplayProcessingModal(true))
      }
    } catch (error) {
      await handleCheckoutConfirmationError(error?.data ?? error)
    }
  }

  const handleClick = async (): Promise<void> => {
    setClicked(true)
    logEvent(TrackingEvent.PRESSED_AUTOPAY_ON_PAGE_CONFIRM_BUTTON, {
      paymentMethodType: PaymentMethodTypes[selectedPaymentMethod?.type as PaymentMethodType],
    })
    dispatch(setStartConfirmTime(performance.now()))
    dispatch(resetPreferredCardReason())

    await dispatch(selectPurchaseMethod(selectedPaymentMethod))

    if (selectedPaymentMethod?.type === PaymentMethodType.APPLE_PAY) {
      applePayHelper.initialise(t, logEvent)
      try {
        await applePayHelper.startSession()
      } catch (e) {
        return
      }
    }

    await finaliseCheckout()
  }

  const ctaText = autoPayAgreed ? t('summary:autopay.modal_paymentMonthly.cta_on') : t('consumerLending:til:cta', { context: isSUPPMOrder ? 'sup' : undefined })

  return (
    <Button
      padding='Fluid'
      loading={clicked}
      onClick={handleClick}
      testNameSpace='truth-in-lending-confirm'
      disabled={clicked}
      aria-label='truth-in-lending-confirm-button'
    >
      {ctaText}
    </Button>
  )
}
